import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
    addAppScrollListener,
    getAppScrollElement,
    getAppScrollTarget,
    getAppScrollTop,
    getAppViewport,
} from './appViewport';

// Keep scroll history outside React state: only a change of bar size rerenders.
export const createBottomMenuScrollState = () => {
    let previous;
    let travel = 0;
    let compact = false;
    return {
        reset() {
            compact = false;
            travel = 0;
        },
        update(geometry, userScrolling) {
            const { contentHeight, viewportHeight, viewportWidth, owner } = geometry;
            const maximum = Math.max(0, contentHeight - viewportHeight);
            const offset = Math.min(maximum, Math.max(0, geometry.offset));
            const changed = !previous || previous.owner !== owner ||
                Math.abs(previous.contentHeight - contentHeight) > 1 ||
                Math.abs(previous.viewportHeight - viewportHeight) > 1 ||
                Math.abs(previous.viewportWidth - viewportWidth) > 1;
            const delta = previous ? offset - previous.offset : 0;
            previous = { contentHeight, viewportHeight, viewportWidth, owner, offset };
            if (maximum <= 1 || offset <= 1) {
                compact = false;
                travel = 0;
            } else if (changed || !userScrolling) {
                travel = 0;
            } else if (delta !== 0) {
                if (delta * travel < 0) travel = 0;
                travel += delta;
                if (travel >= 24) {
                    compact = true;
                    travel = 0;
                } else if (travel <= -16) {
                    compact = false;
                    travel = 0;
                }
            }
            return compact;
        },
    };
};

export const useCompactBottomMenu = (routeKey, frameRef) => {
    const [compact, setCompact] = useState(false);
    const state = useRef(createBottomMenuScrollState());
    const inputUntil = useRef(0);
    const expand = useCallback(() => {
        state.current.reset();
        inputUntil.current = 0;
        setCompact(false);
    }, []);

    useLayoutEffect(() => {
        state.current = createBottomMenuScrollState();
        expand();
        let frame;
        const measure = () => {
            frame = undefined;
            const viewport = getAppViewport();
            const menu = frameRef.current;
            const active = menu && !menu.closest('[hidden], [inert]');
            const userScrolling = active && performance.now() < inputUntil.current;
            setCompact(state.current.update({
                offset: getAppScrollTop(),
                contentHeight: getAppScrollElement().scrollHeight,
                viewportHeight: viewport.height,
                viewportWidth: viewport.width,
                owner: getAppScrollTarget(),
            }, userScrolling));
            // Continue following native touch/trackpad momentum after release.
            if (userScrolling) inputUntil.current = Math.max(inputUntil.current, performance.now() + 200);
        };
        const schedule = () => {
            if (frame === undefined) frame = window.requestAnimationFrame(measure);
        };
        const onInput = event => {
            if (event.type === 'wheel' && Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
            if (event.type === 'pointerdown') {
                // A normal click can launch a programmatic jump. Only a drag
                // on the root scrollbar counts as mouse scroll intent.
                inputUntil.current = 0;
                const element = getAppScrollElement();
                const right = getAppScrollTarget() === window
                    ? window.innerWidth : element.getBoundingClientRect().right;
                if (event.button !== 0 || event.pointerType === 'touch' ||
                    event.target !== element || event.clientX < right - 16) return;
            }
            if (event.type === 'keydown') {
                inputUntil.current = 0;
                if (!['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' '].includes(event.key)) return;
                if (event.target.closest?.('input, textarea, select, button, a[href], [role="button"], [role="tab"], [contenteditable="true"]')) return;
            }
            inputUntil.current = performance.now() + 800;
        };
        const onResize = () => {
            inputUntil.current = 0;
            schedule();
        };
        const removeScrollListener = addAppScrollListener(schedule);
        const inputEvents = ['wheel', 'touchmove', 'pointerdown', 'keydown'];
        inputEvents.forEach(type => window.addEventListener(type, onInput, { capture: true, passive: true }));
        window.addEventListener('resize', onResize);
        window.visualViewport?.addEventListener('resize', onResize);
        measure();
        return () => {
            window.cancelAnimationFrame(frame);
            removeScrollListener();
            inputEvents.forEach(type => window.removeEventListener(type, onInput, true));
            window.removeEventListener('resize', onResize);
            window.visualViewport?.removeEventListener('resize', onResize);
        };
    }, [routeKey, frameRef, expand]);

    return { compact, expand };
};
